/**
 * Cases Service
 *
 * API methods for case management: CRUD, notes, resolution.
 * All methods support abort signals for request cancellation.
 */

import { ApiClient, buildQueryString } from './api';

export class CasesService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  list(params = {}, options = {}) {
    const query = buildQueryString(params);
    return this.client.get(`/cases${query}`, options);
  }

  get(id, options = {}) {
    return this.client.get(`/cases/${id}`, options);
  }

  create(data, options = {}) {
    return this.client.post('/cases', data, options);
  }

  /**
   * Create a case from an alert.
   */
  createFromAlert(alertId, options = {}) {
    return this.client.post(`/cases/from-alert/${alertId}`, {}, options);
  }

  update(id, data, options = {}) {
    return this.client.patch(`/cases/${id}`, data, options);
  }

  resolve(id, resolution, notes = null, options = {}) {
    const body = { resolution };
    if (notes) body.notes = notes;
    return this.client.post(`/cases/${id}/resolve`, body, options);
  }

  addNote(id, content, options = {}) {
    return this.client.post(`/cases/${id}/notes`, { content }, options);
  }

  assign(id, userId, options = {}) {
    return this.client.patch(`/cases/${id}`, { assigned_to: userId }, options);
  }

  /**
   * Escalate a case to a supervisor or compliance officer.
   */
  escalate(id, data, options = {}) {
    return this.client.post(`/cases/${id}/escalate`, data, options);
  }

  getMyCases(params = {}, options = {}) {
    const query = buildQueryString({ ...params, assigned_to: 'me' });
    return this.client.get(`/cases${query}`, options);
  }

  /**
   * Get case counts by status.
   * Returns: { open, in_progress, resolved, total }
   */
  getCounts({ is_sandbox = false, ...options } = {}) {
    const query = is_sandbox ? '?is_sandbox=true' : '';
    return this.client.get(`/cases/counts${query}`, options);
  }

  /**
   * Get timeline/activity for a case.
   */
  getTimeline(id, options = {}) {
    return this.client.get(`/cases/${id}/timeline`, options);
  }

  /**
   * Get available tags for cases.
   */
  getTags(options = {}) {
    return this.client.get('/cases/tags', options);
  }

  /**
   * Add a tag to a case.
   */
  addTag(id, tagId, options = {}) {
    return this.client.post(`/cases/${id}/tags/${tagId}`, {}, options);
  }

  /**
   * Remove a tag from a case.
   */
  removeTag(id, tagId, options = {}) {
    return this.client.delete(`/cases/${id}/tags/${tagId}`, options);
  }

  /**
   * Generate SAR narrative using AI.
   */
  generateSarNarrative(id, activityDetails, options = {}) {
    return this.client.post(`/cases/${id}/sar/narrative`, {
      activity_details: activityDetails,
    }, options);
  }

  /**
   * Generate SAR PDF report. Returns a Blob.
   */
  generateSarReport(id, narrative, activityDetails, options = {}) {
    return this.client.requestBlob(`/cases/${id}/sar/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        narrative,
        activity_details: activityDetails,
      }),
      ...options,
    });
  }

  /**
   * Get SAR report history for a case.
   */
  getSarHistory(id, options = {}) {
    return this.client.get(`/cases/${id}/sar/history`, options);
  }
}

export default CasesService;
